import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Compbaser } from "../../../../components/compbaser";
import { StoreSelectorsService } from "../../../../services";
import { actionUpdateSceneItem } from "../../../../store/actions/user.actions";
import { Id, ISceneBlock, ISceneTreeSliceResults } from "../../../../interfaces";

/**
 * Universal per-item "name" field, shown at the very top of the properties panel for any single
 * selection. It edits the selected item's $.label - the same per-instance name every component uses
 * (Playlist used to call it "playlist name", Scene "label"). The write-back mirrors
 * BlockLayoutCommonPropsComponent.onChange('label').
 *
 * The real scene ROOT is skipped here: it keeps its own name field in the scene panel (its label
 * lives on Player.$.label and the layout panel already treats a scene specially).
 */
@Component({
    selector: 'app-block-name',
    templateUrl: './block-name.component.html',
    styleUrls: ['./block-name.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BlockNameComponent extends Compbaser implements OnInit {
    label = '';
    visible = false;
    // Read-only by default; the input is only rendered while editing. This keeps the field compact
    // and prevents accidental renames from a stray click/keystroke - editing is an explicit action
    // (click the pencil or double-click the name). Enter/blur commits, Esc cancels.
    editing = false;
    private editValue = '';
    private sceneId: Id;
    private sceneItem: ISceneBlock;

    @ViewChild('nameInput') private nameInput: ElementRef<HTMLInputElement>;

    constructor(
        private storeSelector: StoreSelectorsService,
        private cd: ChangeDetectorRef
    ) {
        super();
    }

    ngOnInit() {
        this.cod(
            this.storeSelector.listenPlayerDataOfSelectedSceneItemId()
                .subscribe((slice: ISceneTreeSliceResults) => this.refresh(slice))
        );
    }

    private refresh(slice: ISceneTreeSliceResults) {
        this.sceneId = slice?.sceneId;
        this.sceneItem = slice?.sceneItem;
        // Shown for EVERY single selection - item, group, playlist, nested scene AND the top-level scene
        // (whose name lives on the same $.label). This is the one universal name field, first in the panel.
        this.visible = !!this.sceneItem;
        this.label = this.sceneItem?.$?.label ?? '';
        // A new selection cancels any in-progress edit so we never write a stale name onto another item.
        this.editing = false;
        this.cd.markForCheck();
    }

    startEdit() {
        if (!this.sceneItem) return;
        this.editValue = this.label;
        this.editing = true;
        this.cd.markForCheck();
        // Focus + select the input once it is rendered so the user can type/replace immediately.
        setTimeout(() => {
            const el = this.nameInput?.nativeElement;
            if (el) {
                el.focus();
                el.select();
            }
        });
    }

    commitEdit(value: string) {
        if (!this.editing) return;
        this.editing = false;
        const trimmed = (value ?? '').trim();
        // Nothing meaningful changed - leave the stored label untouched.
        if (!this.sceneItem || trimmed === this.label) {
            this.cd.markForCheck();
            return;
        }
        this.label = trimmed;
        this.storeSelector.dispatch(actionUpdateSceneItem({
            sceneId: this.sceneId,
            sceneItemId: this.sceneItem.$.id,
            values: [{ key: 'label', value: trimmed, storePath: '$' }]
        }));
        this.storeSelector.dispatch({ type: 'BREAK_MERGE' });
        this.cd.markForCheck();
    }

    cancelEdit() {
        this.editing = false;
        this.cd.markForCheck();
    }
}
